import type { Column, FilterFn, Table } from '@tanstack/react-table';
import type { CSSProperties } from 'react';
import type { FilterCondition, FilterValue } from './filter-bar';
import { ACTIVITY_TYPE_EXPANSION } from './filters/activity-filter-config';
import type { PinningShadowState, ServerFilter, ServerFilterCondition } from './types';

// ============================================================================
// Filter Utilities
// ============================================================================

/** Filter value shape used by our custom filter */
interface CustomFilterValue {
  condition: FilterCondition;
  value?: string;
}

/**
 * Custom filter function that handles our filter conditions:
 * contains, not_contains, starts_with, ends_with, is, is_not,
 * greater_than, greater_than_or_equal, less_than, less_than_or_equal, empty, not_empty
 */
export const customFilterFn: FilterFn<unknown> = (
  row,
  columnId,
  filterValue: CustomFilterValue,
) => {
  const { condition, value } = filterValue;

  // Get the cell value and convert to string for comparison
  const cellValue = row.getValue(columnId);
  const cellString = cellValue != null ? String(cellValue).toLowerCase() : '';
  const filterString = value?.toLowerCase() ?? '';

  switch (condition) {
    case 'contains':
      return cellString.includes(filterString);
    case 'not_contains':
      return !cellString.includes(filterString);
    case 'starts_with':
      return cellString.startsWith(filterString);
    case 'ends_with':
      return cellString.endsWith(filterString);
    case 'is':
      return cellString === filterString;
    case 'is_not':
      return cellString !== filterString;
    case 'greater_than':
      return Number(cellValue) > Number(value);
    case 'greater_than_or_equal':
      return Number(cellValue) >= Number(value);
    case 'less_than':
      return Number(cellValue) < Number(value);
    case 'less_than_or_equal':
      return Number(cellValue) <= Number(value);
    case 'empty':
      return cellString === '';
    case 'not_empty':
      return cellString !== '';
    case 'before': {
      const cellDate = new Date(cellString);
      const filterDate = new Date(filterString);
      return cellDate < filterDate;
    }
    case 'after': {
      const cellDate = new Date(cellString);
      const filterDate = new Date(filterString);
      return cellDate > filterDate;
    }
    case 'date_equals': {
      const cellDate = new Date(cellString).toDateString();
      const filterDate = new Date(filterString).toDateString();
      return cellDate === filterDate;
    }
    default:
      return true;
  }
};

/**
 * Maps our UI filter conditions to server API operators
 */
export function mapConditionToOperator(condition: FilterCondition): string {
  switch (condition) {
    case 'contains':
      return 'contains';
    case 'not_contains':
      return 'not_contains';
    case 'starts_with':
      return 'starts_with';
    case 'ends_with':
      return 'ends_with';
    case 'is':
      return 'equals';
    case 'is_not':
      return 'not_equals';
    case 'greater_than':
      return 'greater_than';
    case 'greater_than_or_equal':
      return 'greater_than_or_equal';
    case 'less_than':
      return 'less_than';
    case 'less_than_or_equal':
      return 'less_than_or_equal';
    case 'empty':
      return 'empty';
    case 'not_empty':
      return 'not_empty';
    case 'before':
      return 'date_before';
    case 'after':
      return 'date_after';
    case 'date_equals':
      return 'date_equals';
    case 'from':
      return 'from';
    case 'to':
      return 'to';
    case 'has':
      return 'has';
    default:
      return 'equals';
  }
}

/**
 * Maps server API operators back to UI filter conditions
 */
export function mapOperatorToCondition(operator: string): FilterCondition {
  switch (operator.toLowerCase()) {
    case 'contains':
      return 'contains';
    case 'not_contains':
      return 'not_contains';
    case 'starts_with':
      return 'starts_with';
    case 'ends_with':
      return 'ends_with';
    case 'equals':
      return 'is';
    case 'not_equals':
      return 'is_not';
    case 'greater_than':
      return 'greater_than';
    case 'greater_than_or_equal':
      return 'greater_than_or_equal';
    case 'less_than':
      return 'less_than';
    case 'less_than_or_equal':
      return 'less_than_or_equal';
    case 'empty':
      return 'empty';
    case 'not_empty':
      return 'not_empty';
    case 'before':
    case 'date_before':
      return 'before';
    case 'after':
    case 'date_after':
      return 'after';
    case 'date_equals':
      return 'date_equals';
    case 'from':
      return 'from';
    case 'to':
      return 'to';
    case 'has':
      return 'has';
    default:
      return 'is';
  }
}

/**
 * Builds a ServerFilter from FilterValue array
 * Uses serverField if present, otherwise falls back to columnId
 * Handles value expansion for activity types that map to multiple server values
 */
export function buildServerFilter(filters: FilterValue[]): ServerFilter {
  const conditions: ServerFilterCondition[] = filters
    .filter((f) => f.condition !== null)
    .map((f) => {
      const value = f.value ?? '';
      const expansion = ACTIVITY_TYPE_EXPANSION[value];

      // If this value needs to expand to multiple server values, use the 'in' operator
      if (expansion && expansion.length > 1) {
        return {
          field: f.serverField || f.columnId,
          operator: 'in',
          value: expansion.join(','),
        };
      }

      return {
        field: f.serverField || f.columnId,
        operator: mapConditionToOperator(f.condition!),
        value,
      };
    });

  return { and: conditions };
}

// ============================================================================
// Pinning Utilities
// ============================================================================

export type PinningShadowDirection = 'left' | 'right' | null;

export interface PinningStyles {
  styles: CSSProperties;
  shadowDirection: PinningShadowDirection;
}

/**
 * Gets common CSS styles for pinned columns including positioning
 * Returns shadowDirection separately to allow rendering a gradient-based shadow element
 */
export const getCommonPinningStyles = <TData>(
  column: Column<TData>,
  pinningShadows: PinningShadowState = { left: true, right: true },
): PinningStyles => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');
  const shouldShowLeftShadow = isLastLeftPinnedColumn && pinningShadows.left;
  const shouldShowRightShadow = isFirstRightPinnedColumn && pinningShadows.right;

  // Determine shadow direction for gradient-based shadow element
  const shadowDirection: PinningShadowDirection = shouldShowLeftShadow
    ? 'left'
    : shouldShowRightShadow
      ? 'right'
      : null;

  // Pinned columns need z-index higher than focus:z-10 to prevent focus outlines from overlapping
  return {
    styles: {
      position: isPinned ? 'sticky' : 'relative',
      zIndex: isPinned ? 20 : 0,
    },
    shadowDirection,
  };
};

/**
 * Calculates the left position for a pinned column based on preceding pinned columns
 */
export const getLeftPosition = (columnId: string, table: Table<any>): number => {
  const pinnedLeftColumns = table.getState().columnPinning?.left || [];
  const pinnedIndex = pinnedLeftColumns.indexOf(columnId);

  if (pinnedIndex === -1) {
    return 0;
  }

  let totalWidth = 0;
  for (let i = 0; i < pinnedIndex; i++) {
    const prevColumnId = pinnedLeftColumns[i];
    const prevColumn = table.getAllColumns().find((col) => col.id === prevColumnId);
    totalWidth += prevColumn?.getSize() || 120;
  }
  return totalWidth;
};
